import { Address } from "@/Common/Models/Contacts/Address";
import { Guid } from "@/Common/Models/Guid";
import { LabProcessingStatus } from "@/Common/Models/Lab/Enum/LabProcessingStatus";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { SharedItemWithLabMetadataLink } from "@/Common/Models/Portal/SharedItem/SharedItemWithLabMetadataLink";

class LabMessageGroupDTO {
    public TotalMessages: number;
    public Messages: LabMessageDTO[];
    public ServiceCallerID: number;
    public CurrentPage: number;
}

class LabMessageDTO {
    public ServiceCallerID: number;
    public Order: OrderDTO;
    public Patient: PatientDTO;
    public Clinicians: ClinicianDTO[];
    public LinkedEmptyEncapsulatedData: EncapsulatedDataDTO[];
    public LabFillerID: string;
    public Abnormality: number; // 0 = normal , 1 = abnormal, 2 = critique
    public SendingFacility: string;
    public Context: string;
    public ID: number;
    public MsgUID: string;
    public Description: string;
    public FirstObservation: string;
    public LastObservation: string; // Last Observation Date
    public HasEncapsulatedData: boolean;
    public EmittedDateTime: string; // Message DateTime

    public PrelevmentDateTime: string; // Earliest Prelevment Date
    public SpecimenReceptionDateTime: string;

    public TranscriptionDateTime: string; // Raodiologie Transcription Date
    public FirstInsertInARLMYLEDateTime: string; // Inserted in MYLE database Date
    public LastInsertInARLMYLEDateTime: string;

    public MessageType: string;
    public RequestSubjects: string; // Request SearchString, May be used for Message details in Inbox Later
    public GlobalSubmittedDate: string;
    public GlobalClassfiedDate: string;
    public GlobalIsUnread: boolean;
    public ActionTakenByUnknow: string;
    public ActionTakenByUnknowSplitted: LabActionTaken[];

    public ResolvedLabStatus: string;
    public IsUnreadForQueryingClinician: boolean;
    public OldestTaskNotCompletedInDays: number;
    public LastestTaskCompletionInDays: number;

    public MinDateCreated: Date;
    public MaxDateCompleted: Date;

    public ParentLevel_MyleNoteDtos: MyleNoteDTO[];
    public ParentLevel_MyleActionDtos: MyleActionDTO[];

    public SharedItem: SharedItemWithLabMetadataLink;
    public PortalMessages: SharedItemMessage[];

    public Detail: string;

    public LabProcessingStatus: LabProcessingStatus;
    public RequestNumber: string;
    public IsManualLabEntry: boolean = false;
    public ResultSentDateTime: Date;
}

class PatientDTO {
    public FirstName: string;
    public LastName: string;
    public Sex: string;
    public NAM: string;
    public NamProvinceCode: Guid;
    public MsgUID: string;
    public Phones: TelecomDTO[];
    public Addresses: Address[];
    public NotesList: NoteDTO[];
    public DateOfBirth: string;
}

class ClinicianDTO {
    public Firstname: string;
    public Lastname: string;
    public Prefix: string;
    public Type: number;
    public MsgUID: string;
    public Address: string;
    public ID_Number: string;
    public SubmittedDate: string;
    public ClassfiedDate: string;
    public IsUnread: boolean;
}

class OrderDTO {
    public RequestsList: RequestDTO[];
    public OrderNumber: string;
    public TransactionDateTime: string;
}

class RequestDTO {
    public SetId: string;
    public MaxObservationDatetime_CollectedDatetime: string;
    public ObservationsList: ObservationDTO[];
    public USIT: string; // Description
    public RequestID: string;
    public NotesList: NoteDTO[];
    public ResultStatus: string;
    public SectionIdentifier: string;
}

class ObservationDTO {
    public SetId: string;
    public FlagsList: FlagDTO[];
    public NotesList: NoteDTO[];
    public MyleNotesList: MyleNoteDTO[];
    public MyleActionsList: MyleActionDTO[];

    public Text: string;
    public ResultStatus: string;
    public ValueText: string;
    public Identifier: string;
    public StandardCode: string;
    public AbnormalityFlag: string;
    public PreviousValueText: string;
    public OtherValidValues: boolean;
    public UnitsIdentifier: string;
    public ReferenceRange: string;
    public ObservationID: string;
    public MsgCtrlId: string;
    public Abnormality: number; // 0 = normal , 1 = abnormal, 2 = critique
    public Provider: string;
    public Date: string; // used for charting
    public LabFillerID: string;
}

class FlagDTO {
    public Identifier: string;
}

class LabActionDto {
    public LookupId: string;
    public ActionType: number;
    public OriginalValue: string;
}

class NoteDTO {
    public Comment: string;
}

class MyleNoteDTO {
    public ObservationId: string;
    public LabFillerId: string;
    public Id: string;
    public EmployeeId: string;
    public Comment: string;
    public TimeStamp: Date;
}

class MyleActionDTO {
    public ObservationId: string;
    public LabFillerId: string;
    public Id: string;
    public DateCreated: Date;
    public CompletedOn: Date;
    public OriginalValue: string;
    public ActionLookupId: string;
    public CreatedByEmployeeId: string;
    public CompletedByEmployeeId: string;
    public SharedItemMessageId: string;
}

class EncapsulatedDataDTO {
    public FileName: string;
    public Data: string;
    public Extension: string;
    public DataType: string;
    public Encoding: string;
    public Guid: string;
}

class TelecomDTO {
    public Number: string;
    public IsBusiness: boolean;
}

class OrphanLabForwardDTO {
    public ID: string;
    public DestinataireOriginal: string;
    public ForwardedTo: string;
    public FromDate: string;
    public ToDate: string;
    public LabContextUniversalID: string;
    public CreatedDate: string;
    public ForwardedToName: string;
    public DestinataireOriginalName: string;
}

class LabActionTaken {
    public Action: string;
    public Doctor: string;
    public DoctorLicence: string;
    public ActionDateTime: string;
}

class LabObservationObstetricalFile {
    public Id: number;
    public LabFillerId: string;
    public Identifier: string;
    public Text: string;
    public ValueText: string;
    public Note: string;
    public UnitsIdentifier: string;
    public CollectionDate: Date;
    public ObservationStandardCode: string;
    public ObservationStandardShortDescription: string;
    public RequestSetId: number;
    public ObservationSetId: number;

    constructor(fields?: Partial<LabObservationObstetricalFile>) {
        Object.assign(this, fields);
    }
}

interface IObstetricaLabResultsGroup {
    key: string;
    values: LabObservationObstetricalFile[];
    options: IObstetricaLabResultOption;
}

interface IObstetricaLabResultOption {
    index: number;
    displayTrimester: boolean;
    isValidRegex?: RegExp;
    labObservationResultType?: LabObservationResultType;
    showAsNote?: boolean;
    showResultTypeEvenIfThereIsNoRecentResult?: boolean;
}

enum LabObservationResultType {
    Hemoglobin,
    HBVsurfAg,
    AboRhType,
    AboType,
    RhType,
    Syphilis,
    McvRbc,
    HIV12Ab,
    BldGpAntibody,
    Platelet,
    Chlamydia,
    Gonorrhoeae,
    RubellaIgGAb,
    Glucose1h50g,
    Glucose1h75g,
    Glucose2h75g,
    UrinceCS,
    ChlamydiaGonorrhoeae
}

export {
    ClinicianDTO,
    IObstetricaLabResultOption,
    IObstetricaLabResultsGroup,
    LabActionDto,
    LabActionTaken,
    LabMessageDTO,
    LabMessageGroupDTO,
    LabObservationObstetricalFile,
    LabObservationResultType,
    MyleActionDTO,
    MyleNoteDTO,
    ObservationDTO,
    OrphanLabForwardDTO,
    PatientDTO,
    RequestDTO,
    TelecomDTO
};
